/**
 * TopicPage — SEO hub aggregating courses + blog posts for a broad
 * category like "freeze drying meals". Mounted at `/topics/:slug`.
 * Emits `CollectionPage` JSON-LD so Google understands this is a
 * canonical topic hub, plus a `<PageSEO />` header that surfaces the
 * topic title + description into search snippets.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import PageHero from '../components/PageHero';
import PageSEO from '../components/PageSEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, FileText, Loader2, Compass } from 'lucide-react';

const SITE = 'https://freezedryacademy.com';

const buildCollectionSchema = (topic) => ({
  '@context':   'https://schema.org',
  '@type':      'CollectionPage',
  name:         topic.title,
  description:  topic.description,
  url:          `${SITE}/topics/${topic.slug}`,
  isPartOf:     { '@type': 'WebSite', name: 'Freeze Dry Academy', url: SITE },
  hasPart: [
    ...(topic.courses || []).map((c) => ({
      '@type':      'Course',
      name:         c.title,
      url:          `${SITE}/course/${c.id}`,
      description:  (c.description || '').slice(0, 300),
      provider:     { '@type': 'EducationalOrganization', name: 'Freeze Dry Academy' },
    })),
    ...(topic.posts || []).map((p) => ({
      '@type':      'Article',
      headline:     p.title,
      url:          `${SITE}/blog/${p.slug}`,
      description:  p.excerpt || p.subtitle || '',
    })),
  ],
});

const TopicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { API } = useContext(AuthContext);
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/topics/${slug}`);
        if (!cancelled) setTopic(data);
      } catch { if (!cancelled) setTopic(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [API, slug]);

  const schema = useMemo(() => (topic ? buildCollectionSchema(topic) : null), [topic]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!topic) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6">
        <div className="text-lg font-heading font-extrabold">Topic not found</div>
        <Button variant="outline" onClick={() => navigate('/platform/learning')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to catalog
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="topic-page">
      <PageSEO
        title={topic.title}
        description={topic.description}
        path={`/topics/${slug}`}
        type="website"
        schema={schema}
      />
      <PageHero
        eyebrow="Topic hub"
        eyebrowIcon={Compass}
        title={topic.title}
        description={topic.description}
        testId="topic-hero"
      />
      <div className="container mx-auto max-w-5xl px-4 py-8 space-y-8">
        {/* Courses */}
        <section data-testid="topic-courses">
          <h2 className="font-heading text-2xl font-extrabold mb-3 inline-flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" /> Courses ({topic.counts.courses})
          </h2>
          {topic.courses.length === 0 ? (
            <p className="text-sm text-muted-foreground">No courses tagged with this topic yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {topic.courses.map((c) => (
                <Link
                  key={c.id}
                  to={`/course/${c.id}`}
                  className="block group"
                  data-testid={`topic-course-${c.id}`}
                >
                  <Card className="transition hover:shadow-lg hover:-translate-y-0.5">
                    <CardContent className="p-4 flex gap-3">
                      {(c.thumbnail_url || c.hero_image_url) && (
                        <img src={c.thumbnail_url || c.hero_image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-extrabold group-hover:text-primary line-clamp-2">{c.title}</div>
                        {c.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Blog posts */}
        <section data-testid="topic-posts">
          <h2 className="font-heading text-2xl font-extrabold mb-3 inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" /> Articles ({topic.counts.posts})
          </h2>
          {topic.posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No articles tagged with this topic yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {topic.posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="block group"
                  data-testid={`topic-post-${p.slug}`}
                >
                  <Card className="transition hover:shadow-lg hover:-translate-y-0.5">
                    {p.hero_image_url && <img src={p.hero_image_url} alt="" className="w-full h-32 object-cover" />}
                    <CardContent className="p-3">
                      <div className="font-heading font-extrabold text-sm group-hover:text-primary line-clamp-2">{p.title}</div>
                      {p.excerpt && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{p.excerpt}</p>}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TopicPage;
